import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { balanceSheetsQueryKey } from "./financials.keys";
import { renameBalanceSheetViewLabel } from "./utils/patchBalanceSheetView";

export function useRenameBalanceSheetLineItem(accountId: SuretyAccountId) {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["renameBalanceSheetLineItem", accountId],
		mutationFn: (body: Dtos.Admin.BalanceSheet.RenameLineItem.Request) =>
			patch<void>(`/v2/admin/surety/accounts/${accountId}/balance-sheets/line-items`, body, "none"),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
			);
			queryClient.setQueryData<Dtos.Admin.BalanceSheet.List.Response>(
				balanceSheetsQueryKey(accountId),
				(cached) =>
					cached?.map((sheet) =>
						body.balanceSheetIds.includes(sheet.id) && sheet.view
							? {
									...sheet,
									view: renameBalanceSheetViewLabel(
										sheet.view,
										body.sectionPath,
										body.oldLabel,
										body.newLabel,
									),
								}
							: sheet,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(balanceSheetsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (queryClient.isMutating({ mutationKey: ["renameBalanceSheetLineItem", accountId] }) <= 1) {
				queryClient.invalidateQueries({ queryKey: balanceSheetsQueryKey(accountId) });
			}
		},
	});

	return {
		renameBalanceSheetLineItem: mutation.mutateAsync,
	};
}
